import { Observable, Subscription } from 'rxjs';
import { ToastrService } from 'ngx-toastr';

import { Component, OnInit, Inject } from '@angular/core';

import { AngularFirestore, AngularFirestoreCollection } from '@angular/fire/compat/firestore';

import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FormGroup, FormControl, FormBuilder, Validators, AbstractControl } from '@angular/forms';

import { moveItemInArray, CdkDragDrop } from "@angular/cdk/drag-drop";

import { SpinnerService } from '../../../services/spinner.service';
import { ReservationFacilitySpecial } from '../../../firestore-models/reservation-facility-special';

import { ApiReservationFacilitySpecial, ApiSetReservationFacilitySpecialsRequest } from '../../../api-client-module';

import { ConfigurationService } from '../../../configuration.service';
import { ApiClientService } from '../../../api-client.service';
import { AppConst } from '../../../app-const';
import { TranslateService } from '@ngx-translate/core';
import { DateAdapter, NativeDateAdapter } from '@angular/material/core';

export interface ReservationFacilitySpecialDialogData {
  change_type: number;
  reservationFacilitySpecial?: ApiReservationFacilitySpecial;
}

export interface marker {
  lat: number;
  lng: number;
  draggable: boolean;
}

@Component({
  selector: 'app-reservation-facility-special-dialog',
  templateUrl: './reservation-facility-special-dialog.component.html',
  styleUrls: ['./reservation-facility-special-dialog.component.css'],
  standalone: false,
  providers: [
    // このコンポーネントとその子要素専用のDateAdapterインスタンスを作成 //(0703)
    { provide: DateAdapter, useClass: NativeDateAdapter },
  ]
})
export class ReservationFacilitySpecialDialogComponent implements OnInit {

  //ReservationFacilityコレクション
  private reservationFacilitySpecialsCollection!: AngularFirestoreCollection<ReservationFacilitySpecial>;
  reservationFacilitySpecialsObservable!: Observable<ReservationFacilitySpecial[]>;
  reservationFacilitySpecialsSubscribe!: Subscription;
  reservationFacilitySpecials!: Array<ReservationFacilitySpecial>;
  // 特別営業日情報
  reservationFacilitySpecial?: ApiReservationFacilitySpecial;
  // 各特別営業日情報に紐付くプロジェクトID
  projectId: string;
  reservationFacilityId: string;
  reservationUnit: number;

  isAdded: boolean = false;

  // 特別営業日情報フォーム
  reservationFacilitySpecialFormGroup!: FormGroup;
  reservationSpecialDateTypeControl!: FormControl;
  reservationSpecialStartDateControl!: FormControl;
  reservationSpecialEndDateControl!: FormControl;
  reservationSpecialCountControl!: FormControl;

  inReservationFacilitySpecialId = '';

  reservationSpecialDateTypes = AppConst.RESERVATION_SPECIAL_DATE_TYPES;
  hours = AppConst.HOURS;
  minutes = AppConst.MINUTES;

  currentLang: string = 'ja';
  private langChangeSub!: Subscription; // TranslateServiceの購読を管理


  readonly RESERVATION_SPECIAL_DATE_TYPE_SPECIAL: number = AppConst.RESERVATION_SPECIAL_DATE_TYPE_SPECIAL;
  readonly RESERVATION_UNIT_TIME: number = AppConst.RESERVATION_UNIT_TIME;

  constructor(
    public dialogRef: MatDialogRef<ReservationFacilitySpecialDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ReservationFacilitySpecialDialogData,
    public dialog: MatDialog,
    private angularFirestore: AngularFirestore,
    private formBuilder: FormBuilder,
    private configurationService: ConfigurationService,
    private translate: TranslateService,
    private apiClientService: ApiClientService,
    private spinnerService: SpinnerService,
    private toastr: ToastrService,
    private dateAdapter: DateAdapter<any>
  ) {
    this.projectId = this.configurationService.getProjectId();
    this.reservationFacilityId = this.configurationService.getReservationFacilityId();
    this.reservationUnit = this.configurationService.getReservationUnit();
  }

  ngOnInit(): void {
    this.currentLang = this.translate.currentLang; //(0703)
    // 1. コンポーネント初期化時に現在の言語でカレンダーを設定
    this.setCalendarLocale(this.currentLang);

    // 2. アプリケーションの言語が変更されたら、カレンダーの言語も動的に追従させる
    this.langChangeSub = this.translate.onLangChange.subscribe(event => {
      this.setCalendarLocale(event.lang);
    });
    this.reservationFacilitySpecial = this.data.reservationFacilitySpecial;
    if (!this.reservationFacilitySpecial) {
      this.inReservationFacilitySpecialId = this.getUniqueStr();
      this.reservationFacilitySpecial = {
        reservation_facility_special_id: '',
        reservation_facility_id: this.reservationFacilityId,
        project_id: this.projectId,
        reservation_special_date_type: AppConst.RESERVATION_SPECIAL_DATE_TYPE_HOLIDAY,
        reservation_special_start_date: null as any,
        reservation_special_end_date: null as any,
        reservation_special_count: null as any,
        reservation_select_times: [],
        enabled: true,
      };
      this.isAdded = true;
      this.currentLang = this.translate.currentLang;
    } else {
      this.inReservationFacilitySpecialId = this.reservationFacilitySpecial.reservation_facility_special_id;
    }

    this.createForm();
    this.reservationSpecialDateTypeControl = this.reservationFacilitySpecialFormGroup.get('reservationSpecialDateType') as FormControl;
    this.reservationSpecialStartDateControl = this.reservationFacilitySpecialFormGroup.get('reservationSpecialStartDate') as FormControl;
    this.reservationSpecialEndDateControl = this.reservationFacilitySpecialFormGroup.get('reservationSpecialEndDate') as FormControl;
    this.reservationSpecialCountControl = this.reservationFacilitySpecialFormGroup.get('reservationSpecialCount') as FormControl;
    // 特別営業日取得
    this.reservationFacilitySpecialsCollection = this.angularFirestore.collection<ReservationFacilitySpecial>(
      AppConst.FIRESTORE_RESERVATION_FACILITY_SPECIALS, ref => ref.where('project_id', '==', this.projectId).where('reservation_facility_id', '==', this.reservationFacilityId).orderBy('reservation_special_start_date', 'asc'));
    this.reservationFacilitySpecialsObservable = this.reservationFacilitySpecialsCollection.valueChanges({ idField: 'id' });
    this.reservationFacilitySpecialsSubscribe = this.reservationFacilitySpecialsObservable.subscribe(reservationFacilitySpecials => {
      for (let reservationFacilitySpecial of reservationFacilitySpecials) {
        this.reservationFacilitySpecials = reservationFacilitySpecials;
      }
    });
  }

  /** //(0703)
  * Datepickerのロケールを設定するプライベートメソッド
  * @param lang 設定したい言語コード (例: 'en', 'ja')
  */
  private setCalendarLocale(lang: string): void {
    // 言語に応じたロケールを設定
    const locale = lang === 'en' ? 'en-US' : 'ja-JP';
    this.dateAdapter.setLocale(locale);
  }
  
  //以下、フォームの記入エラー表示
  public getErrorMessageToReservationSpecialStartDate() {
    return this.reservationSpecialStartDateControl.hasError('required')
      ? this.translate.instant('必須項目')
      : '';
  }

  public getErrorMessageToReservationSpecialEndDate() {
    return this.translate.instant('予約可能開始日より前の日は設定不可');
  }

  public getErrorMessageToReservationSpecialCount() {
    return this.reservationSpecialCountControl.hasError('pattern')
      ? this.translate.instant('0以上を入力')
      : '';
  }

  // フォームの値を各要素に代入
  private createForm() {
    this.reservationFacilitySpecialFormGroup = this.formBuilder.group(
      {
        reservationSpecialDateType: [this.reservationFacilitySpecial!.reservation_special_date_type, [Validators.required]],
        reservationSpecialStartDate: [this.reservationFacilitySpecial!.reservation_special_start_date, [Validators.required]],
        reservationSpecialEndDate: [this.reservationFacilitySpecial!.reservation_special_end_date],
        reservationSpecialCount: [this.reservationFacilitySpecial!.reservation_special_count, [Validators.pattern('^[0-9]+$')]],
      }, {
      validator: this.isValidReservationDate
    });
  }

  // 更新ボタン
  addReservationFacilitySpecial(): void {
    if (this.reservationFacilitySpecialFormGroup.valid) {
      let error_flag = false;
      if (this.reservationUnit === AppConst.RESERVATION_UNIT_TIME && this.reservationSpecialDateTypeControl.value === AppConst.RESERVATION_SPECIAL_DATE_TYPE_SPECIAL && this.reservationFacilitySpecial!.reservation_select_times.length === 0) {
        alert(this.translate.instant('時間枠を入力してください。'));
        return;
      }
      for (const reservation_select_time of this.reservationFacilitySpecial!.reservation_select_times) {
        if (reservation_select_time.items.length > 0) {
          if (!reservation_select_time.items || reservation_select_time.items.length !== 4 || reservation_select_time.items.includes('')) {
            error_flag = true;
          }
          if (!reservation_select_time.value || reservation_select_time.value == '') {
            error_flag = true;
          } else {
            if (reservation_select_time.value < 0) {
              error_flag = true;
            }
          }
        } else {
          error_flag = true;
        }
      }
      if (!!error_flag) {
        alert(this.translate.instant('時間枠の入力に誤りがあります。'));
        return;
      }
      if (this.reservationUnit === AppConst.RESERVATION_UNIT_PERIOD && this.reservationSpecialDateTypeControl.value === AppConst.RESERVATION_SPECIAL_DATE_TYPE_SPECIAL) {
        if (!this.reservationSpecialCountControl.value || this.reservationSpecialCountControl.value == '') {
          alert(this.translate.instant('最大予約数の入力に誤りがあります。'));
          return;
        }
      }
      if (this.reservationFacilitySpecials) {
        if (this.reservationSpecialStartDateControl.value) {
          if (this.reservationSpecialEndDateControl.value) {
            for (let reservationFacilitySpecial of this.reservationFacilitySpecials) {
              if (reservationFacilitySpecial.id === this.reservationFacilitySpecial!.id) {
                continue;
              }
              if (reservationFacilitySpecial.reservation_special_start_date) {
                let searchStartDate = new Date(reservationFacilitySpecial.reservation_special_start_date?.seconds * 1000);
                if (reservationFacilitySpecial.reservation_special_end_date) {
                  let searchEndDate = new Date(reservationFacilitySpecial.reservation_special_end_date?.seconds * 1000);
                  if (new Date(this.reservationSpecialStartDateControl.value).getTime() <= searchEndDate.getTime() && new Date(this.reservationSpecialEndDateControl.value).getTime() >= searchStartDate.getTime()) {
                    alert(this.translate.instant('期間重複しているデータがあります。'));
                    return;
                  }
                } else {
                  if (new Date(this.reservationSpecialStartDateControl.value).getTime() <= searchStartDate.getTime() && searchStartDate.getTime() <= new Date(this.reservationSpecialEndDateControl.value).getTime()) {
                    alert(this.translate.instant('期間重複しているデータがあります。'));
                    return;
                  }
                }
              }
            }
          } else {
            for (let reservationFacilitySpecial of this.reservationFacilitySpecials) {
              if (reservationFacilitySpecial.id === this.reservationFacilitySpecial!.id) {
                continue;
              }
              if (reservationFacilitySpecial.reservation_special_start_date) {
                let searchStartDate = new Date(reservationFacilitySpecial.reservation_special_start_date?.seconds * 1000);
                if (reservationFacilitySpecial.reservation_special_end_date) {
                  let searchEndDate = new Date(reservationFacilitySpecial.reservation_special_end_date?.seconds * 1000);
                  if (searchStartDate.getTime() <= new Date(this.reservationSpecialStartDateControl.value).getTime() && new Date(this.reservationSpecialStartDateControl.value).getTime() <= searchEndDate.getTime()) {
                    alert(this.translate.instant('期間重複しているデータがあります。'));
                    return;
                  }
                } else {
                  if (searchStartDate.getTime() == new Date(this.reservationSpecialStartDateControl.value).getTime()) {
                    alert(this.translate.instant('期間重複しているデータがあります。'));
                    return;
                  }
                }
              }
            }
          }
        }
      }
      if (this.reservationSpecialEndDateControl.value) {
        if (new Date(this.reservationSpecialStartDateControl.value).getTime() === new Date(this.reservationSpecialEndDateControl.value).getTime()) {
          this.reservationSpecialEndDateControl.setValue(null);
        }
      }
      this.reservationFacilitySpecial!.reservation_facility_special_id = this.inReservationFacilitySpecialId;
      this.reservationFacilitySpecial!.reservation_facility_id = this.reservationFacilityId;
      this.reservationFacilitySpecial!.reservation_special_date_type = this.reservationSpecialDateTypeControl.value;
      this.reservationFacilitySpecial!.reservation_special_start_date = this.reservationSpecialStartDateControl.value ? this.reservationSpecialStartDateControl.value : null;
      this.reservationFacilitySpecial!.reservation_special_end_date = this.reservationSpecialEndDateControl.value ? this.reservationSpecialEndDateControl.value : null;
      this.reservationFacilitySpecial!.reservation_special_count = this.reservationSpecialCountControl.value;
      if (this.reservationFacilitySpecial) {
        let request: ApiSetReservationFacilitySpecialsRequest = {
          access_token: '',
          change_type: this.data.change_type,
          reservation_facility_specials: [this.reservationFacilitySpecial]
        }
        this.spinnerService.openSpinner();
        this.apiClientService.setReservationFacilitySpecials(request).then(
          response => {
            if (response!.ret === AppConst.API_RET_SUCCESS) {
              this.toastr.success(this.translate.instant('登録しました。'));
              this.dialogRef.close();
            } else if (response!.ret === AppConst.API_RET_ALREADY_EXIST_DATA) {
              this.toastr.error(this.translate.instant('既に同じIDが登録済みです。'), '', { timeOut : 5000 });
            } else {
              console.error('api update error. ret='+response!.ret);
              this.toastr.error(this.translate.instant('登録できませんでした。'), '', { timeOut : 5000 });
            }
          }
        ).catch(
          error => {
            console.log(error);
            this.toastr.error(this.translate.instant('登録できませんでした。'), '', { timeOut : 5000 });
          }
        ).finally(
          () => {
            this.spinnerService.closeSpinner();
          }
        );
      }
    }
  }

  onNoClick(): void {
    this.dialogRef.close();
  }

  // 予約可能期間整合制チェック
  isValidReservationDate(ac: AbstractControl) {
    const startDate = ac.get('reservationSpecialStartDate');
    const endDate = ac.get('reservationSpecialEndDate');
    endDate?.setErrors(null);
    if (!!startDate && !!endDate && !!startDate.value && !!endDate.value) {
      if (startDate.value > endDate.value) {
        endDate.setErrors({ before_start_date: true });
      }
    }
  }

  clearReservationSpecialStartDate(): void {
    this.reservationSpecialStartDateControl.setValue(null);
  }

  clearReservationSpecialEndDate(): void {
    this.reservationSpecialEndDateControl.setValue(null);
  }

  onSelectValueChange(event: any, index: number): void {
    this.reservationFacilitySpecial!.reservation_select_times![index].value = event.target.value;
  }

  onSelectItemChange(index: number, item_index: number, value: string): void {
    this.reservationFacilitySpecial!.reservation_select_times![index].items[item_index] = value;
  }

  // 予約枠追加ボタン
  appendClick(): void {
    if (!this.reservationFacilitySpecial!.reservation_select_times) {
      this.reservationFacilitySpecial!.reservation_select_times = new Array();
    }
    this.reservationFacilitySpecial!.reservation_select_times!.push({
      index: this.getUniqueStr(),
      items: ['00', '00', '00', '00'],
      value: '',
    });
  }

  // 予約枠削除ボタン
  deleteClick(index: number): void {
    this.reservationFacilitySpecial!.reservation_select_times!.splice(index, 1);
  }

  // 予約枠並べ替え
  onDropSelectItem(event: CdkDragDrop<string[]>) {
    moveItemInArray(this.reservationFacilitySpecial!.reservation_select_times, event.previousIndex, event.currentIndex);
  }

  getUniqueStr() {
    var strong = 1000;
    return new Date().getTime().toString(16) + Math.floor(strong * Math.random()).toString(16) + Math.floor(strong * Math.random()).toString(16)
  }
}
